import {
  SET_AGENT,
  SET_LOADING,
  SET_USER,
  SIGN_OUT,
  SIGN_OUT_AGENT,
  UPDATE_USER
} from "../actions/auth.actions";
import { CLEAR_SIMULATED_USER, SET_SIMULATED_DATA } from '../actions/config.actions';
import axios from "../utils/axiosConfig";
import queryString from 'query-string';

/**
 * @typedef AuthStore
 * @property {string} agentToken
 * @property {boolean} agentReady
 * @property {number} userReady
 * @property {Agent} agent
 * @property {User} user
 */

const queryParams = queryString.parse(window.location.search);

/** @type {AuthStore} */
const initialState = {
  user: JSON.parse(localStorage.getItem("user")),
  userData: JSON.parse(localStorage.getItem("user")),
  agent: JSON.parse(localStorage.getItem("agent")),
  userReady: false,
  agentReady: false,
  loading: true,
  isInfluencerWithOfferAccessToken: /^\/influencers\/offer\/\d+.*/.test(window.location.pathname) && queryParams.token,
};

/**
 * Setup headers in axios for current user type
 * @param {string} id
 * @param {string} token
 * @returns {void}
 */
const setupAuthorization = (id, token) =>
  axios.defaults.headers.common.Authorization = `Bearer ${id}:${token}`;

export default function authReducer(state = initialState, action) {
  let newUser = null;

  switch (action.type) {
    case SET_LOADING:
      return {
        ...state,
        loading: action.payload,
      };
    case SET_USER:
      if (action.payload) {
        localStorage.setItem("user", JSON.stringify(action.payload));
      }
      return {
        ...state,
        user: action.payload,
        userReady: true,
      };
    case SET_AGENT:
      if (action.payload) {
        localStorage.setItem("agent", JSON.stringify(action.payload));
      }
      return {
        ...state,
        agent: action.payload,
        agentReady: true,
      };
    case UPDATE_USER:
      newUser = {
        ...state.user,
        ...action.payload,
      };
      localStorage.setItem("user", JSON.stringify(newUser));
      return {
        ...state,
        user: newUser,
      };
    case SIGN_OUT:
      return {
        ...state,
        user: null,
      };
    case SIGN_OUT_AGENT:
      return {
        ...state,
        agent: null
      };
    // If a user is simulated, set the user data to the simulated data
    // keeps the original user data in the store so once the simulated user is cleared
    // the original user data is restored
    case SET_SIMULATED_DATA:
      return {
        ...state,
        user: action.payload,
      };
    case CLEAR_SIMULATED_USER:
      return {
        ...state,
        user: state.userData,
      };
    default:
      return state;
  }
}


/**
 * @typedef Agent
 * @property {number} id
 * @property {boolean} admin
 * @property {string} email
 * @property {string} name
 * @property {number} account_id
 * @property {string} account_name
 * @property {object[]} brands
 * @property {number} brands.id
 * @property {string} brands.name
 * @property {string|null} brands.description
 * @property {string|null} brands.pic
 */

/**
 * @typedef {object} User
 * @property {number} id
 * @property {string} first_name
 * @property {string} last_name
 * @property {string} phone
 * @property {string} email
 * @property {string} paypal_email
 * @property {string} gender
 * @property {string} best_pic
 * @property {number} age
 * @property {null} corgi_count
 * @property {boolean} admin
 * @property {string} marital_status
 * @property {string} sexual_orientation
 * @property {string} ethnicity
 * @property {string} nationality
 * @property {number} city_id
 * @property {string} dob
 * @property {null} yout_hash_error
 * @property {bool} sales_admin
 * @property {bool} csm_admin
 * @property {bool} partner_manager
 * @property {bool} super_admin
 * @property {bool} analytics_admin
 * @property {bool} sales_admin
 * @property {bool} user_management_admin
 * @property {string} currency
 * @property {string} currency_symbol
 * @property {string} corgi_payment_per_redemption
 * @property {number} corgi_redeem_bucket
 * @property {number} unredeemed_corgi_count
 * @property {string} corgi_earned_so_far
 * @property {string} corgi_earned_total
 * @property {object} object_attribute_values
 * @property {EditableAttributes[]} editable_attributes
 * @property {UserAddress} primary_address
 * @property {UserAddress} address
 * @property {SocialProfile[]} social_profiles
 * @property {string} fullname
 * @property {UserSocial} social
 * @property {boolean} tos_accepted
 * @property {boolean} european
 * @property {boolean} salesforce_oauth_token
 * @property {boolean} yout_oauth_token
 * @property {null} yout_oauth_provide
 * @property {object[]} user.posts
 * @property {string} user.posts.thumb_url
 * @property {string} user.posts.url
 * @property {null} user.posts.story
 * @property {object[]} user.instagram_posts
 * @property {string} user.instagram_posts.thumb_url
 * @property {string} user.instagram_posts.url
 * @property {null} user.instagram_posts.story
 * @property {object[]} user.tiktok_posts
 * @property {string} user.tiktok_posts.thumb_url
 * @property {string} user.tiktok_posts.url
 * @property {null} user.tiktok_posts.story
 * @property {} user.youtube_posts
 * @property {object[]} user.priority_platform_posts
 * @property {string} user.priority_platform_posts.thumb_url
 * @property {string} user.priority_platform_posts.url
 * @property {null} user.priority_platform_posts.story
 */

/**
 * @typedef {UserLight}
 * @property {number} id
 * @property {string} email
 * @property {string} gender
 * @property {string} best_pic
 * @property {number} age
 * @property {string} first_name
 * @property {null} admin
 * @property {string} fullname
 * @property {object} social
 * @property {object} social.inst
 * @property {string} social.inst.nickname
 * @property {object} social.yout
 * @property {string} social.yout.nickname
 * @property {object} social.tikt
 * @property {string} social.tikt.nickname
 * @property {object} social.pint
 * @property {string} social.pint.nickname
 * @property {object} social.twit
 * @property {string} social.twit.nickname
 */


/**
 * @typedef UserAddress
 * @property {number} id
 * @property {string} first_name
 * @property {string} last_name
 * @property {string} address
 * @property {string} address_2
 * @property {string} city
 * @property {string} country
 * @property {string} zip
 * @property {string} phone
 * @property {string} state
 */

/**
 * @typedef SocialProfile
 * @property {number} id
 * @property {string} platform
 * @property {string} ext_id
 * @property {number} user_id
 * @property {string} username
 * @property {string} profile_pic
 * @property {string} external_url
 * @property {string} fullname
 * @property {object} stats
 * @property {null} stats.audience_by_age
 * @property {null} stats.audience_by_city
 * @property {null} stats.audience_by_country
 * @property {null} stats.audience_by_gender
 * @property {null} stats.audience_by_state
 * @property {null} stats.authentic_followers
 * @property {null} stats.avg_comments
 * @property {null} stats.avg_emv
 * @property {null} stats.avg_followers
 * @property {null} stats.avg_likes
 * @property {null} stats.avg_shares
 * @property {null} stats.avg_views
 * @property {null} stats.bio
 * @property {null} stats.comments_to_views_ratio
 * @property {null} stats.eng_rate
 * @property {} stats.follower_growth_180_days
 * @property {} stats.follower_growth_30_days
 * @property {} stats.follower_growth_364_days
 * @property {null} stats.followers
 * @property {null} stats.hash_tags
 * @property {null} stats.last_video_posted_at
 * @property {null} stats.likes_to_views_ratio
 * @property {null} stats.monthly_active_followers
 * @property {null} stats.post_count
 * @property {null} stats.sixty_day_view_average
 * @property {null} stats.tags
 * @property {string} stats.username
 * @property {null} stats.view_to_subscriber_ratio
 * @property {null} stats.youtube_videos
 */

/**
 * @typedef UserSocial
 * @property {SocialObj} inst
 * @property {SocialObj} yout
 * @property {SocialObj} tikt
 * @property {SocialObj} pint
 * @property {SocialObj} twit
 * @property {SocialObj} blog
 * @property {SocialObj} podc
 */

/**
 * @typedef SocialObj
 * @property {string} nickname
 * @property {number} followers
 * @property {string?} url
 * @property {string?} platform
 * @property {string?} rss_link
 * @property {null?} umv
 * @property {string} bio
 * @property {number} posts
 * @property {number} quality_rating
 * @property {number} emv
 * @property {string} avg_eng_rate
 * @property {string} us_audience
 * @property {object} gender_ratio
 * @property {string} gender_ratio.male
 * @property {string} gender_ratio.female
 * @property {number} avg_likes
 * @property {number} fake_ratio
 * @property {string} partner_account_country
 * @property {number} partner_account_country_audience
 * @property {string} external_url
 * @property {number} avg_views
 * @property {number} view_to_follower_ratio
 * @property {string} last_video_at
 */

/**
 * @typedef EditableAttributes
 * @property {number} id
 * @property {string} label
 * @property {string} code
 * @property {boolean} is_multiple
 * @property {boolean} has_options
 * @property {string} datatype
 * @property {null} ui_element
 * @property {null} optional
 * @property {null} is_editable_by_client
 * @property {null} is_visible_to_client
 * @property {object[]} options
 * @property {number} options.id
 * @property {string} options.label
 * @property {null} options.i18_code
 * @property {string} object_type_form_string
 * @property {string} i18_code
 * @property {boolean} is_editable_by_influencer
 * @property {boolean} is_visible_to_influencer
 * @property {null} column_label
 *
 */
